import {
  EnvironmentOutlined,
  LoadingOutlined,
  LockOutlined,
  LogoutOutlined
} from "@ant-design/icons";
import { Button } from "antd";
import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";

import AIImageComparisonTool from "./components/aiImageComparisonTool/AIImageComparisonTool";
import { canAccessProjectTeamPage } from "./constants/users";
import { useUser } from "./contexts/UserContext";
import ApprovalsPage from "./pages/ApprovalsPage";
import DashboardPage from "./pages/DashboardPage";
import ForecastingOverviewPage from "./pages/ForecastingOverviewPage";
import ForecastingPage from "./pages/ForecastingPage";
import SiteOpsMobilePrivacyPolicyPage from "./pages/SiteOpsMobilePrivacyPolicyPage";
import WhatsOnSitePage from "./pages/WhatsOnSitePage";
import WhosOnSitePage from "./pages/WhosOnSitePage";
import { AppProvider } from "./provider/app";
import { logOut } from "./utils/auth";

import DocumentLibraryPage from "@/features/document-library";
import ProjectTeamPage from "@/features/project-team";

import "./styles/design-system.css";

const PUBLIC_PATHS = ["/siteops-mobile/privacy-policy"];

// A data router (rather than <BrowserRouter>) so pages can use `useBlocker`
// to confirm before navigating away. The routes themselves stay declarative
// in <Routes> below.
const router = createBrowserRouter([{ path: "*", element: <AppRouter /> }]);

function App() {
  return <RouterProvider router={router} />;
}

function AppRouter() {
  const location = useLocation();

  if (PUBLIC_PATHS.includes(location.pathname)) {
    return (
      <Routes>
        <Route
          path="/siteops-mobile/privacy-policy"
          element={<SiteOpsMobilePrivacyPolicyPage />}
        />
      </Routes>
    );
  }

  return (
    <AppProvider>
      <AuthenticatedRouter />
    </AppProvider>
  );
}

function AuthenticatedRouter() {
  const user = useUser();

  if (user?.loading) {
    return (
      <div className="flex h-full items-center justify-center gap-x-1.5 p-[100px]">
        <LoadingOutlined />
        <span>Loading...</span>
      </div>
    );
  }

  if (user?.fetchSuccess) {
    if (user.hasAccess) {
      const canAccessProjectTeam = canAccessProjectTeamPage(
        user.roleDisplayName
      );

      return (
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/documents" element={<DocumentLibraryPage />} />

          <Route path="/forecasting" element={<ForecastingOverviewPage />} />

          <Route
            path="/forecasting/:opportunityNo/:projectDesc/:month"
            element={<ForecastingPage />}
          />

          <Route
            path="/project-team"
            element={
              canAccessProjectTeam ? (
                <ProjectTeamPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          <Route path="whats-on-site" element={<WhatsOnSitePage />} />

          <Route path="whos-on-site" element={<WhosOnSitePage />} />

          <Route path="/approvals" element={<ApprovalsPage />} />

          <Route
            path="/ai-image-comparison"
            element={<AIImageComparisonTool />}
          />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      );
    } else if (!!user.roleDisplayName?.trim() && !user.hasRegion) {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="bg-white rounded-xl shadow-md px-12 py-10 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                <EnvironmentOutlined className="text-blue-500 text-2xl" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              Almost There
            </h2>
            <p className="text-gray-500 text-sm mb-1">
              Your account is set up, but it still needs a region assigned
              before you can access this module.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Ask your system administrator to add a region to your account - it
              only takes a moment.
            </p>
            <Button
              type="primary"
              icon={<LogoutOutlined />}
              onClick={logOut}
              size="large"
            >
              Logout
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="bg-white rounded-xl shadow-md px-12 py-10 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                <LockOutlined className="text-red-500 text-2xl" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              Access Denied
            </h2>
            <p className="text-gray-500 text-sm mb-1">
              Your account has not been configured as part of the Project Team
              and therefore you do not have the required permissions to access
              this module.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Please contact your system administrator to have your account set
              up with the appropriate permissions.
            </p>
            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={logOut}
              size="large"
            >
              Logout
            </Button>
          </div>
        </div>
      );
    }
  }

  return null;
}

// Exported (in addition to the default App) so the access-gate logic can be
// unit-tested without mounting AppProvider/MSAL.
export { AuthenticatedRouter };
export default App;
